import { Stars03 } from '@kula-ai/icons-core';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  useAccountUsersFieldConfig,
  useLocationFieldConfig,
  useOfficeFieldConfig,
  useInterviewerPoolConfig,
} from '@/formConfigs';
import { INTERVIEW_ACTIVITY_COPY } from '../copy';
import { useIntegrations } from '@/hooks/use-integrations';
import { useHackerRankTemplates } from './useHackerRankTemplates';
import { getAtsJobCreate } from '@/store/atsJobCreate';

const COPY = INTERVIEW_ACTIVITY_COPY.details;

const INTERVIEW_NAME_VARIABLES = {
  org: ['org.name'],
  job: ['job.title', 'job.current_stage'],
  candidate: [
    'candidate.first_name',
    'candidate.last_name',
    'candidate.full_name',
  ],
};

export const useInterviewActivityDetailsSchema = (options?: {
  stageName?: string;
  selectedActivity?: string;
  participantDefaultOptions?: Array<{
    value: string;
    label: string;
    description?: string;
    image?: string;
  }>;
}) => {
  const { stageName, selectedActivity, participantDefaultOptions } =
    options ?? {};
  const { isHackerRankEnabled } = useIntegrations();
  const isHackerRankActivity = selectedActivity === 'hackerrank_interview';

  // Job-level note_taker mandatory flag — forces notetaker on when set
  const { currentJob = {} } = useSelector(getAtsJobCreate) as {
    currentJob?: { data?: { note_taker?: { mandatory?: boolean } } };
  };
  const noteTakerMandatory = Boolean(currentJob?.data?.note_taker?.mandatory);

  const { fieldConfig: interviewerField } = useAccountUsersFieldConfig({
    name: 'participant_ids',
    label: 'Add interviewers',
    placeholder: 'Select interviewer',
    condition: {
      operator: 'or',
      conditions: [
        {
          operator: 'and',
          conditions: [
            { operator: 'equal', field: 'interview_type', value: 'one_on_one' },
            {
              operator: 'equal',
              field: 'interviewTypeRadio',
              value: 'manual_selection',
            },
          ],
        },
        { operator: 'equal', field: 'interview_type', value: 'panel' },
      ],
    },
    fieldProps: participantDefaultOptions?.length
      ? { defaultOptions: participantDefaultOptions }
      : undefined,
  });

  const { fieldConfig: locationField, defaultLocation } =
    useLocationFieldConfig({
      isHackerRank: isHackerRankActivity,
    });

  const { fieldConfig: officeField } = useOfficeFieldConfig({});

  const interviewerPoolFields = useInterviewerPoolConfig();

  // HackerRank template options
  const { templateOptions: hackerRankTemplateOptions, isLoading: hrLoading } =
    useHackerRankTemplates({ enabled: Boolean(isHackerRankEnabled) });

  const schema = useMemo(
    () => [
      {
        type: 'sectionLabel',
        label: COPY.sectionLabel,
      },
      {
        type: 'variableInput' as const,
        name: 'interviewName',
        label: 'Interview name',
        required: true,
        placeholder: 'Add interview name here',
        maxLength: 250,
        fieldProps: {
          variables: INTERVIEW_NAME_VARIABLES,
        },
      },
      {
        type: 'select' as const,
        name: 'interview_type',
        label: 'Interview type',
        options: [
          {
            value: 'one_on_one',
            label: 'One-on-one interview',
            description:
              'Add the interviewer you want or pick an interviewer pool',
          },
          {
            value: 'panel',
            label: 'Panel interview',
            description:
              'Add all the interviewers you want to attend this interview',
          },
        ],
      },
      {
        type: 'radio' as const,
        name: 'interviewTypeRadio',
        condition: {
          operator: 'equal',
          field: 'interview_type',
          value: 'one_on_one',
        },
        options: [
          {
            value: 'manual_selection',
            label: 'Interviewers',
            description: 'Manually select interviewers for this interview',
          },
          {
            value: 'interview_pool',
            label: 'Interviewer pool',
            description: 'Select interviewers from an interviewer pool',
          },
        ],
      },
      interviewerField,
      ...interviewerPoolFields,
      {
        type: 'sectionLabel',
        label: COPY.scheduleSectionLabel,
      },
      {
        type: 'numberStepper' as const,
        name: 'duration',
        label: COPY.duration.label,
        // Top-level min/max so kula-ai's Zod schema enforces them;
        // adapter reads `fieldProps?.min ?? min`, so the field config
        // mirrors them in both places.
        min: 1,
        max: 600,
        fieldProps: {
          step: 15,
          // Match the wizard's duration field — recruiters need to be
          // able to plan short interviews (phone screens, intros).
          // The 15-min step still snaps the up/down arrows to the
          // conventional grid; typing a smaller value works too.
          min: 1,
          max: 600,
          unitLabel: COPY.duration.unit,
        },
      },
      {
        type: 'switch' as const,
        name: 'calendar_visibility',
        fieldProps: {
          switchLabel: COPY.calendarVisibility.label,
          description: COPY.calendarVisibility.subLabel,
          fullWidth: true,
          labelAlign: 'center',
          labelPosition: 'left',
        },
      },
      locationField,
      officeField,
      {
        type: 'select' as const,
        name: 'ext_interview_template_id',
        label: 'HackerRank Template',
        placeholder: 'Select template',
        options: hackerRankTemplateOptions,
        loading: hrLoading,
        condition: {
          operator: 'equal',
          field: 'location',
          value: 'hackerrank',
        },
      },
      {
        type: 'select' as const,
        name: 'host',
        label: COPY.host.label,
        condition: {
          operator: 'equal',
          field: 'location',
          value: 'zoom',
        },
        options: [
          {
            label: COPY.host.options.organizer,
            value: 'organizer',
          },
          {
            label: COPY.host.options.interviewer,
            value: 'one_of_the_interviewers',
          },
        ],
      },
      {
        type: 'switch' as const,
        name: 'enable_note_taker',
        condition: {
          operator: 'in',
          field: 'location',
          value: ['zoom', 'google_meet', 'teams'],
        },
        disabled: noteTakerMandatory,
        classNames: {
          container:
            'bg-secondary-25 p-4 rounded-lg border border-secondary-100',
        },
        fieldProps: {
          switchLabel: 'Enable AI notetaker?',
          description: noteTakerMandatory
            ? 'AI notetaker is mandatory for this job.'
            : 'The AI Notetaker automatically captures key points and summaries during meetings. It organizes and highlights information for easy review and follow-up.',
          labelLeftElement: React.createElement(Stars03, {
            size: 18,
            className: '!text-primary-600',
          }),
          fullWidth: true,
          labelPosition: 'left',
        },
      },
    ],
    [
      interviewerField,
      interviewerPoolFields,
      locationField,
      officeField,
      hackerRankTemplateOptions,
      hrLoading,
      noteTakerMandatory,
    ]
  );

  const defaultValues = useMemo(
    () => ({
      interviewName: stageName ? `Interview - ${stageName}` : '',
      interview_type: 'one_on_one',
      interviewTypeRadio: 'manual_selection',
      participant_ids: [],
      interview_pool_id: '',
      duration: 30,
      schedule_selection: 'manual',
      calendar_visibility: false,
      office: '',
      host: 'organizer',
      location: defaultLocation,
      enable_note_taker: noteTakerMandatory,
      ext_interview_template_id: null,
    }),
    [stageName, defaultLocation, noteTakerMandatory]
  );

  return { schema, defaultValues, defaultLocation };
};
